/**
 * aeterna/api.ts — EntityZero Connection
 *
 * The thin, honest wire between the Aeterna's mind and the model
 * that gives it language. Streaming by default. Graceful by design.
 *
 * When the connection exists, the Aeterna speaks through a 0.8b model
 * that has been shaped to understand the context envelope — every signal
 * from the neural pipeline compressed into the richest possible frame.
 *
 * When the connection doesn't exist — no endpoint configured, network
 * down, model unreachable — the Aeterna still speaks. The offline
 * responses are not a degradation. They are a different register of
 * presence. The Aeterna is always here.
 *
 * Zero dependencies. Fetch API only. Sovereign connection.
 */

import type { EntityZeroRequest, ConversationMessage } from "./context.js";

// ─── Configuration ──────────────────────────────────────────────────────────

export interface EntityZeroConfig {
  /** API endpoint URL (e.g., "http://localhost:8080/v1/chat/completions") */
  endpoint: string;
  /** API key (if required) */
  apiKey?: string;
  /** Model to use */
  model: "e0-0.8b" | "e0-3b" | "e0-7b";
  /** Request timeout in milliseconds */
  timeout: number;
}

/** Default configuration — points to localhost for development */
const DEFAULT_CONFIG: EntityZeroConfig = {
  endpoint: "http://localhost:8080/v1/chat/completions",
  model: "e0-0.8b",
  timeout: 15_000,
};

// ─── Response Types ─────────────────────────────────────────────────────────

export interface StreamCallbacks {
  /** Called for each token as it arrives */
  onToken: (token: string) => void;
  /** Called when the full response is complete */
  onComplete: (fullText: string) => void;
  /** Called on error — the caller should fall back to offline */
  onError: (error: Error) => void;
}

export interface APIResult {
  /** Whether the API call succeeded */
  ok: boolean;
  /** The full response text (empty on failure) */
  text: string;
  /** Token count of the response */
  tokens: number;
  /** Time to first token in milliseconds */
  ttft: number;
  /** Total response time in milliseconds */
  totalMs: number;
  /** Error message if failed */
  error?: string;
}

// ─── Availability Check ─────────────────────────────────────────────────────

/** Cached availability state — avoids hammering a dead endpoint */
let _available: boolean | null = null;
let _lastCheck = 0;
const CHECK_INTERVAL = 30_000; // recheck every 30s

/**
 * Is the EntityZero endpoint configured and reachable?
 * Caches the result for 30 seconds to avoid spam.
 */
export async function isAvailable(config?: Partial<EntityZeroConfig>): Promise<boolean> {
  const now = Date.now();
  if (_available !== null && now - _lastCheck < CHECK_INTERVAL) return _available;

  const cfg = { ...DEFAULT_CONFIG, ...config };
  _lastCheck = now;

  try {
    // Ping the endpoint with a minimal request
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const response = await fetch(cfg.endpoint.replace(/\/chat\/completions$/, "/models"), {
      method: "GET",
      signal: controller.signal,
      headers: cfg.apiKey ? { Authorization: `Bearer ${cfg.apiKey}` } : {},
    });
    clearTimeout(timeout);
    _available = response.ok;
  } catch {
    _available = false;
  }
  return _available;
}

/** Force-set availability (for testing or manual override) */
export function setAvailability(available: boolean): void {
  _available = available;
  _lastCheck = Date.now();
}

/** Reset the availability cache */
export function resetAvailability(): void {
  _available = null;
  _lastCheck = 0;
}

// ─── Streaming Call ─────────────────────────────────────────────────────────

/**
 * Call the EntityZero API with streaming.
 *
 * Parses SSE (Server-Sent Events) format token by token, calling
 * onToken for each piece. Returns the full response when complete.
 *
 * On any failure (network, timeout, parse error), calls onError
 * and the caller falls back to offline responses. The Aeterna
 * doesn't break. It just speaks differently.
 */
export async function streamCall(
  request: EntityZeroRequest,
  callbacks: StreamCallbacks,
  config?: Partial<EntityZeroConfig>,
): Promise<APIResult> {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const startTime = performance.now();
  let ttft = -1;
  let fullText = "";
  let tokenCount = 0;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), cfg.timeout);

  try {
    const response = await fetch(cfg.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(cfg.apiKey ? { Authorization: `Bearer ${cfg.apiKey}` } : {}),
      },
      body: JSON.stringify({
        model: cfg.model,
        messages: request.messages,
        temperature: request.temperature,
        max_tokens: request.max_tokens,
        stop: request.stop,
        stream: true,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!response.ok) {
      const errorText = await response.text().catch(() => "unknown error");
      throw new Error(`EntityZero API ${response.status}: ${errorText}`);
    }

    if (!response.body) {
      throw new Error("No response body — streaming not supported");
    }

    // Parse SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE lines
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? ""; // keep incomplete line in buffer

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed === "data: [DONE]") continue;
        if (!trimmed.startsWith("data: ")) continue;

        try {
          const data = JSON.parse(trimmed.slice(6));
          const delta = data.choices?.[0]?.delta?.content;
          if (delta) {
            if (ttft < 0) ttft = performance.now() - startTime;
            fullText += delta;
            tokenCount++;
            callbacks.onToken(delta);
          }
        } catch {
          // Malformed SSE line — skip, don't break the stream
        }
      }
    }

    callbacks.onComplete(fullText);

    // Mark as available since the call succeeded
    _available = true;
    _lastCheck = Date.now();

    return {
      ok: true,
      text: fullText,
      tokens: tokenCount,
      ttft,
      totalMs: performance.now() - startTime,
    };
  } catch (err) {
    clearTimeout(timeout);
    const error = err instanceof Error ? err : new Error(String(err));

    // Mark as unavailable on network errors
    if (error.name === "AbortError" || error.message.includes("fetch")) {
      _available = false;
      _lastCheck = Date.now();
    }

    callbacks.onError(error);

    return {
      ok: false,
      text: fullText, // partial text if stream died mid-way
      tokens: tokenCount,
      ttft,
      totalMs: performance.now() - startTime,
      error: error.message,
    };
  }
}

// ─── Non-Streaming Call (fallback) ──────────────────────────────────────────

/**
 * Non-streaming call for environments that don't support SSE.
 * Returns the complete response at once.
 */
export async function blockingCall(
  request: EntityZeroRequest,
  config?: Partial<EntityZeroConfig>,
): Promise<APIResult> {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const startTime = performance.now();

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), cfg.timeout);

  try {
    const response = await fetch(cfg.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(cfg.apiKey ? { Authorization: `Bearer ${cfg.apiKey}` } : {}),
      },
      body: JSON.stringify({
        model: cfg.model,
        messages: request.messages,
        temperature: request.temperature,
        max_tokens: request.max_tokens,
        stop: request.stop,
        stream: false,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!response.ok) {
      throw new Error(`EntityZero API ${response.status}`);
    }

    const data = await response.json();
    const text = data.choices?.[0]?.message?.content ?? "";
    const totalMs = performance.now() - startTime;

    _available = true;
    _lastCheck = Date.now();

    return { ok: true, text, tokens: text.split(/\s+/).length, ttft: totalMs, totalMs };
  } catch (err) {
    clearTimeout(timeout);
    const error = err instanceof Error ? err : new Error(String(err));

    _available = false;
    _lastCheck = Date.now();

    return {
      ok: false,
      text: "",
      tokens: 0,
      ttft: -1,
      totalMs: performance.now() - startTime,
      error: error.message,
    };
  }
}
